/**
 * Deep link handler
 *
 * Web: handles /join/:code routes (from QR code scan or share sheet)
 * Native (Capacitor): listens to App plugin appUrlOpen events
 */
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase.js';

/** Used on the /join/:code route — validates the code and pre-fills the join screen */
export const useDeepLink = () => {
  const navigate = useNavigate();
  const { code } = useParams();

  useEffect(() => {
    if (!code) return;

    const autoJoin = async () => {
      const clean = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (clean.length !== 6) {
        toast.error('Invalid session code');
        navigate('/join', { replace: true });
        return;
      }

      try {
        const { data: session, error } = await supabase
          .rpc('get_session_preview_by_code', { p_code: clean });

        if (error || !session) {
          toast.error(`Session "${clean}" not found`);
          navigate('/join', { replace: true });
          return;
        }

        if (session.status === 'settled' || session.status === 'archived') {
          toast.error('This session has already been settled');
          navigate('/', { replace: true });
          return;
        }

        navigate(`/join?code=${session.code}`, {
          replace: true,
          state:   { autoJoin: true, session },
        });
      } catch {
        navigate('/join', { replace: true });
      }
    };

    autoJoin();
  }, [code, navigate]);
};

/**
 * Capacitor App plugin deep link listener.
 * Uses the global window.Capacitor bridge — no static import needed.
 * Handles:
 *   - https://splitsnap.app/join/ABC123
 *   - web+splitsnap://join?code=ABC123
 */
export const useCapacitorDeepLink = (navigate) => {
  useEffect(() => {
    // Only active in a Capacitor native context
    if (!window.Capacitor?.isNativePlatform?.()) return;

    const Plugins = window.Capacitor?.Plugins;
    if (!Plugins?.App) return;

    let listenerHandle = null;

    Plugins.App.addListener('appUrlOpen', ({ url }) => {
      try {
        const parsed = new URL(url);

        // https://splitsnap.app/join/ABC123
        const pathMatch = parsed.pathname.match(/\/join\/([A-Z0-9]{6})/i);
        if (pathMatch) {
          navigate(`/join?code=${pathMatch[1].toUpperCase()}`);
          return;
        }

        // web+splitsnap://join?code=ABC123
        if (parsed.hostname === 'join') {
          const urlCode = parsed.searchParams.get('code');
          if (urlCode) navigate(`/join?code=${urlCode.toUpperCase()}`);
        }
      } catch {
        // Malformed URL — ignore
      }
    }).then((handle) => { listenerHandle = handle; });

    return () => { listenerHandle?.remove?.(); };
  }, [navigate]);
};
